import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { DictKey, DictShape } from './en';
import { detectLocale, locales, resolveKey } from './index';

type LocaleContextValue = {
  locale: string;
  setLocale: (code: string) => void;
};

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  setLocale: () => undefined,
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => detectLocale());

  // The route is the source of truth for the UI language (see detectLocale): the
  // active locale is driven by the current `/{lang}/` URL, not persisted UI storage,
  // so the switcher can never drift from the page you are on.
  const setLocale = useCallback((code: string) => {
    if (locales.some((l) => l.code === code)) {
      setLocaleState(code);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

export function useT(): (key: DictKey, vars?: Record<string, string | number>) => string {
  const { locale } = useLocale();
  const dict: Partial<DictShape> = useMemo(
    () => locales.find((l) => l.code === locale)?.dict ?? {},
    [locale],
  );

  return useCallback(
    (key: DictKey, vars?: Record<string, string | number>) => resolveKey(dict, key, vars),
    [dict],
  );
}
